/**
 * Dashboard version selector — controls whether Home renders the new
 * Penpot-matched dashboard or the legacy line-graph one.
 *
 * Behavior:
 *   - The new dashboard is what everyone opens on, in every environment.
 *   - The toggle stays, so anyone who prefers the legacy view can switch back;
 *     that choice is remembered in localStorage and wins over the default.
 *
 * The environment no longer affects which view opens first. Production used to
 * start on the legacy dashboard while every other environment started on the
 * new one, which meant the thing being rolled out was the thing customers never
 * saw.
 *
 * Once the legacy dashboard is retired, delete this file and revert Home to
 * `return <Dashboard />`.
 */

import { useCallback, useState } from "react";

const STORAGE_KEY = "eserv:dashboard-version";

export type DashboardVersion = "old" | "new";

const DEFAULT_VERSION: DashboardVersion = "new";

export function useDashboardVersion() {
  const [version, setVersionState] = useState<DashboardVersion>(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored === "new" || stored === "old" ? stored : DEFAULT_VERSION;
    } catch {
      return DEFAULT_VERSION;
    }
  });

  const setVersion = useCallback((next: DashboardVersion) => {
    setVersionState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage unavailable — keep in-memory only */
    }
  }, []);

  return { version, setVersion };
}
